import { useState } from "react";
import { Volume2, Loader2, Languages, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { setAudioPlaying } from "@/lib/audioLock";

const DEMO_TEXT = {
  en: "Let's solve 2x plus 5 equals 15. First, subtract 5 from both sides to isolate the x term.",
  hi: "Chalo 2x plus 5 equals 15 solve karte hain. Pehle, dono sides se 5 subtract karo x term ko isolate karne ke liye.",
};

const DEMOS = [
  { key: "en_riya", label: "English (Riya)", lang: "en", icon: Sparkles },
  { key: "en_standard", label: "English (Classic)", lang: "en", icon: Volume2 },
  { key: "hi_mixed", label: "Hindi-Mixed Voice", lang: "hi", icon: Languages },
];

export default function LandingVoiceDemo() {
  const [status, setStatus] = useState("idle");
  const [active, setActive] = useState(null);

  const playDemo = async (demo) => {
    if (status !== "idle") return;
    setActive(demo.key);
    setStatus("loading");
    try {
      const { data } = await api.post("/voice/tts", { text: DEMO_TEXT[demo.lang], narration_voice: demo.key });
      setAudioPlaying(true);
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => { setAudioPlaying(false); setStatus("idle"); };
      audio.onerror = () => { setAudioPlaying(false); setStatus("idle"); };
      setStatus("playing");
      await audio.play();
    } catch {
      setAudioPlaying(false);
      setStatus("idle");
    }
  };

  return (
    <section id="voice-demo" className="py-10 sm:py-16 bg-white" data-testid="landing-voice-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-300/60 rounded-full px-3 py-1 text-xs font-bold" data-testid="voice-demo-first-badge">
            <Languages size={12} /> Industry First · Hybrid OpenAI + ElevenLabs Voices
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-3">Choose the voice that feels like home.</h2>
          <p className="text-muted-foreground mt-3">
            Every lesson is spoken aloud. Switch anytime in Settings between three voices — including a warm
            Hindi-mixed tutor that keeps technical words in English, just like your own tuition teacher:{" "}
            <span className="font-notebook text-foreground">"numerator को बदलना है"</span>.
          </p>
          <div className="flex flex-wrap gap-3 mt-6" data-testid="voice-demo-buttons">
            {DEMOS.map((demo) => (
              <button
                key={demo.key}
                onClick={() => playDemo(demo)}
                disabled={status !== "idle"}
                data-testid={`voice-demo-play-${demo.key}-button`}
                className="inline-flex items-center gap-2 bg-card border-2 border-border font-bold px-4 sm:px-5 py-3 rounded-full hover:border-primary/50 active:scale-95 transition-all disabled:opacity-60 text-sm"
              >
                {status === "loading" && active === demo.key ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <demo.icon size={15} className="text-primary" />
                )}
                {status === "playing" && active === demo.key ? "Playing..." : demo.label}
              </button>
            ))}
          </div>
        </div>
        <div className="relative" data-testid="voice-demo-visual">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/efb5525d-e484-4894-a0d3-6f8b4f2b6938/images/ea63db17dfe219186ed90bb9f93b02875360e98c4fff13e1b52f57cdf5788b88.jpeg"
            alt="Choose between three voice narration options"
            className="rounded-3xl shadow-lg w-full object-cover aspect-[4/5]"
          />
        </div>
      </div>
    </section>
  );
}
